import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from "react-helmet-async";
import { Navbar, Footer, Breadcrumbs } from "../../components/shared";
import { useCheckAccess, convertTimestampToDate, formatINR } from "../../utils/Utilities";
import { viewExpense, approveExpense as approveExpenseApi, markPaidExpense as markPaidApi } from "../../service/ExpenseApi";
import { queryExpenseCategoryConfig } from "../../service/UtilApi";
import { useLocation } from 'react-router-dom';
import '../general/General.css';
import { useSelector } from "react-redux";
const ExpensesSearch = () => {
  useCheckAccess('ExpenseRO');
  const user = useSelector((state) => state.user);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialCategory = queryParams.get('category') || '';
  const initialSubcategory = queryParams.get('subcategory') || '';

  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState({
    familyId: '',
    category: initialCategory,
    subcategory: initialSubcategory,
    startDate: '',
    endDate: '',
  });

  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categoryError, setCategoryError] = useState('');
  const [error, setError] = useState('');

  // Role flags
  const [canApprove, setCanApprove] = useState(false);
  const [canMarkPaid, setCanMarkPaid] = useState(false);

  useEffect(() => {
    if (user) {
      setCanApprove(user.roles?.includes('ExpenseApprove'));
      setCanMarkPaid(user.roles?.includes('ExpensePay'));
    }
  }, [user]);

  const fetchCategories = async () => {

    try {
      setLoading(true);
      const response = await queryExpenseCategoryConfig();
      if (response?.categoriesList) {
        setCategories(response.categoriesList);
      } else {
        setCategoryError('வகைகள் இல்லை');
      }
    } catch (err) {
      console.error(err);
      setCategoryError('வகைகளை பெற முடியவில்லை');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const selectedCategory = categories.find(cat => cat.categoryName === filters.category);
  const subcategories = selectedCategory?.subcategories || [];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'category' && { subcategory: '' }),
    }));
  };

  const fetchExpenses = useCallback(async () => {
    const { category, subcategory, startDate, endDate, familyId } = filters;

    if (!category || !subcategory) {
      setError("தயவுசெய்து வகையும் துணைவகையும் தேர்வு செய்யவும்");
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await viewExpense({
        category,
        subcategory,
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...(familyId && { familyId }),
      });

      if (res?.expenses) {
        setExpenses(res.expenses);
      } else {
        setError("Failed to load expenses.");
      }
    } catch (err) {
      console.error(err);
      setError("Error fetching expenses");
    } finally {
      setLoading(false);
    }
  }, [filters]);


  const approveExpense = async (expenseId) => {
    if (!expenseId || !filters.category || !filters.subcategory) return;

    try {
      await approveExpenseApi({
        expenseId,
        category: filters.category,
        subcategory: filters.subcategory,
      });
      fetchExpenses();
    } catch (err) {
      alert('Failed to approve expense');
    }
  };

  const markPaidExpense = async (expenseId) => {
    if (!expenseId || !filters.category || !filters.subcategory) return;

    try {
      await markPaidApi({
        expenseId,
        category: filters.category,
        subcategory: filters.subcategory,
      });
      fetchExpenses();
    } catch (err) {
      alert('செலவினம் பணம் செலுத்தப்பட்டது என குறிக்க முடியவில்லை');
    }
  };


  useEffect(() => {
    if (initialCategory && initialSubcategory) {
      fetchExpenses();
    }
  }, [initialCategory, initialSubcategory, fetchExpenses]);

  return (
    <div>
      <Navbar />
      <Helmet><title>செலவினங்களை பார்க்க</title></Helmet>
      <div className="general_parent">
        <div className="general_container">
          <div className="general_body">
            <Breadcrumbs />
            <h2>செலவினங்களை பார்க்க</h2>

            <div className="auth_element">
              {/* Form Inputs */}
              <label className="auth_label">
                வகை:
                <select
                  className="auth_input"
                  name="category"
                  value={filters.category}
                  onChange={handleChange}
                >
                  <option value="">-- ஒரு வகையைத் தேர்வு செய்க --</option>
                  {categories.map(cat => (
                    <option key={cat.categoryName} value={cat.categoryName}>
                      {cat.description}
                    </option>
                  ))}
                </select>
              </label>

              <label className="auth_label">
                துணைவகை:
                <select
                  className="auth_input"
                  name="subcategory"
                  value={filters.subcategory}
                  onChange={handleChange}
                  disabled={!filters.category}
                >
                  <option value="">-- துணைவகையைத் தேர்வு செய்க --</option>
                  {subcategories.map(sub => (
                    <option key={sub.name} value={sub.name}>
                      {sub.description || sub.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="auth_label">
                தேதி முதல்:
                <input
                  type="date"
                  name="startDate"
                  className="auth_input"
                  value={filters.startDate}
                  onChange={handleChange}
                />
              </label>

              <label className="auth_label">
                தேதி வரை:
                <input
                  type="date"
                  name="endDate"
                  className="auth_input"
                  value={filters.endDate}
                  onChange={handleChange}
                />
              </label>

              <button className="auth_button" onClick={fetchExpenses} disabled={loading}>
                {loading ? 'ஏற்றுகிறது…' : 'செலவுகளை பார்வையிடுக'}
              </button>

              {error && <p style={{ color: 'red' }}>{error}</p>}
              {categoryError && <p style={{ color: 'red' }}>{categoryError}</p>}
            </div>

            {/* Table */}
            {expenses.length > 0 ? (
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>

                      <th>செலவு விவரம்</th>
                      <th>தொகை</th>
                      <th>தேதி</th>
                      <th>நிலை</th>
                      <th>நடவடிக்கை</th>
                      <th>சேர்த்தவர்</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expenses.map((exp) => (
                      <tr key={exp.expenseId}>

                        <td>{exp.categoryDescription + '-' + exp.subCategoryDescription + ' ' + exp.comments + '-' + exp.externalTransactionReference}</td>
                        <td style={{ textAlign: 'right' }}> {`${formatINR(exp.amount)}`}</td>
                        <td>{convertTimestampToDate(exp.transactionDate).toLocaleDateString()}</td>
                        <td>{exp.status}</td>
                        <td>
                          {exp.status === 'pending' && canApprove && (
                            <button
                              className="auth_button small"
                              onClick={() => approveExpense(exp.expenseId)}
                            >
                              Approve
                            </button>
                          )}
                          {exp.status === 'approved' && canMarkPaid && (
                            <button
                              className="auth_button small"
                              onClick={() => markPaidExpense(exp.expenseId)}
                            >
                              Mark Paid
                            </button>
                          )}
                        </td>
                        <td>{exp.createdByFullName}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              !loading && <p>எந்த செலவுகளும் இல்லை.</p>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ExpensesSearch;
